import { useEffect, useRef } from "react";
import { cn } from "@/lib/cn";

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  /** Stagger index (1–5) — adds animation delay in 100ms increments */
  stagger?: 1 | 2 | 3 | 4 | 5;
  /** Render as a section element instead of div */
  asSection?: boolean;
}

export default function ScrollReveal({
  children,
  className,
  stagger,
  asSection,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const delayClass = stagger ? `delay-${stagger * 100}` : undefined;
  const classes = cn("scroll-reveal", delayClass, className);

  if (asSection) {
    return (
      <section ref={ref} className={classes}>
        {children}
      </section>
    );
  }

  return (
    <div ref={ref} className={classes}>
      {children}
    </div>
  );
}
